/**
 * Caller id presentation for the softphone widget.
 *
 * Telnyx hands the WebRTC client a bare E.164-ish string (`16197884358`); the agent gets the
 * formatted number, and only after the call has been up for a while - see `MASKED_CALL_SECONDS` in
 * the widget for why the start of a call shows the masked form instead.
 */

const MASK_CHARACTER = '•'

// The digits an agent reads back to a caller. Everything before them is masked while a call is new.
const VISIBLE_DIGITS = 4

/**
 * `16197884358` -> `+1 (619) 788-4358`. Anything that is not a NANP number is returned as it came:
 * an international caller, a SIP username or an alphanumeric caller id is still more useful to show
 * verbatim than to force into a shape it does not have.
 */
export function formatCallerId(callerNumber) {
  if (!callerNumber) {
    return ''
  }

  const raw = String(callerNumber).trim()
  const digits = raw.replace(/\D/g, '')

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }

  if (digits.length === 10) {
    return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }

  return raw
}

/**
 * Masks every digit of the FORMATTED number except the last four, keeping the punctuation so the
 * shape still reads as a phone number: `+1 (619) 788-4358` -> `+• (•••) •••-4358`.
 */
export function maskCallerId(callerNumber) {
  const formatted = formatCallerId(callerNumber)

  if (!formatted) {
    return ''
  }

  // Walk backwards so the last four alphanumerics survive wherever the punctuation happens to fall.
  let visible = 0

  return formatted
    .split('')
    .reverse()
    .map(character => {
      if (!/[a-z0-9]/i.test(character)) {
        return character
      }

      if (visible < VISIBLE_DIGITS) {
        visible += 1

        return character
      }

      return MASK_CHARACTER
    })
    .reverse()
    .join('')
}

export default { formatCallerId, maskCallerId }
